import React, { useState } from 'react';
import { useLogo, logoConfigs } from '../contexts/LogoContext';
import { branding } from '../config/branding';
import { useTenant } from '../contexts/TenantContext';

interface ChessDirectorLogoProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showText?: boolean;
  textColor?: string;
  knightGlow?: string;
  overrideVariant?: 'default' | 'tournament' | 'player' | 'dashboard' | 'find' | 'browse';
}

const ChessDirectorLogo: React.FC<ChessDirectorLogoProps> = ({
  className = '',
  size = 'md',
  showText = true,
  textColor,
  knightGlow,
  overrideVariant
}) => {
  const { tenant } = useTenant();

  // Check if app is installed as PWA
  const isPWAInstalled = typeof window !== 'undefined' && (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone ||
    document.referrer.includes('android-app://')
  );

  // Try to use logo context, fallback to default if not available
  let logoConfig = logoConfigs.default;
  try {
    const context = useLogo();
    logoConfig = context.logoConfig;
  } catch (e) {
    // Context not available, use default
  }

  // Use override if provided, otherwise use context
  let config = overrideVariant
    ? (logoConfigs[overrideVariant] || logoConfig)
    : logoConfig;

  // For website (non-PWA), keep variant styling but always use company logo icon
  // UNLESS we are in a tenant context
  if (tenant) {
    const tenantLogo = tenant.branding?.logoUrl || tenant.logoUrl;
    if (tenantLogo) {
      config = {
        ...config,
        icon: tenantLogo,
        name: tenant.branding?.name || tenant.name || config.name
      };
    }
  } else if (!isPWAInstalled) {
    config = {
      ...config,
      icon: branding.logos.main, // Always use company logo icon
      // Keep variant name/subtitle for styling, but override Tournament to company name
      name: config.name === 'Tournament' ? branding.companyName : config.name,
      subtitle: config.subtitle === 'LIVE RESULTS' ? branding.companyTagline : config.subtitle
    };
  }

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-12 w-12',
    lg: 'h-16 w-16',
    xl: 'h-20 w-20'
  };

  const textSizeClasses = {
    sm: 'text-lg font-bold',
    md: 'text-2xl font-bold',
    lg: 'text-3xl font-bold',
    xl: 'text-4xl font-bold'
  };

  // Use consistent styling from branding config (no variant colors)
  const finalTextColor = textColor || branding.theme.textColor;
  const finalKnightGlow = knightGlow || '';

  const [logoError, setLogoError] = useState(false);

  // Logic identifying the source. 
  // If we are in a tenant context, and we have an error or empty config icon, 
  // DO NOT fallback to branding.logos.main (ChessDirector).
  // Instead, use a placeholder or null.
  const logoSource = (!tenant && logoError) ? branding.logos.main : config.icon;

  // Determine if we should show the image tag
  // Show if:
  // 1. We have a valid logoSource (not empty)
  // 2. AND (we are not in error state OR we aren't a tenant)
  //    If we are a tenant and have an error, we hide the image to avoid showing broken link or fallback.
  const showImage = logoSource && (!logoError || !tenant);

  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      {/* Logo Image with Clean Styling */}
      {showImage ? (
        <div className="relative group">
          <img
            src={logoSource}
            alt={`${config.name} Logo`}
            className={`${sizeClasses[size]} object-contain drop-shadow-md transition-transform group-hover:scale-105`}
            onError={() => {
              if (!logoError) {
                setLogoError(true);
              }
            }}
            onLoad={() => {
              setLogoError(false);
            }}
          />
        </div>
      ) : (
        // Fallback placeholder for tenant if no logo
        tenant && (
          <div className={`${sizeClasses[size]} flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200`}>
            <span className="text-gray-500 font-bold text-xs uppercase">
              {config.name ? config.name.substring(0, 2) : 'Org'}
            </span>
          </div>
        )
      )}

      {/* Text with Modern Typography */}
      {showText && (
        <div className="flex flex-col">
          <span className={`${finalTextColor} ${textSizeClasses[size]}`}>
            {config.name}
          </span>
          {config.subtitle && (
            <span className={`text-gray-600 font-semibold tracking-wide ${size === 'sm' || size === 'md' ? 'text-xs' : size === 'lg' ? 'text-sm' : 'text-base'}`}>
              {config.subtitle}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ChessDirectorLogo;
